import type { Transaction } from 'kysely';
import type { LocalDatabase } from '../core/types';
import type { Database, MealLogRecord } from '~/powersync/generated/schema';
import { RandomUtils } from '@fytness/shared';
import {
  MealLogCopyPlanner,
  type MealLogCopyDestination,
  type MealLogCopyMode,
  type MealLogCopyPlan,
  type MealLogCopyResult,
  type MealLogPlacement,
  type MealLogSummaryRow,
} from './meal-log-copy';

type MealLogTransaction = Transaction<Database>;

/**
 * Applies a plan's deletes, updates and inserts on an already-open transaction
 * handle, in that order. Shared by every write path so the ordering is defined
 * in exactly one place.
 */
const applyCopyPlanIn = async (
  trx: MealLogTransaction,
  plan: MealLogCopyPlan,
): Promise<void> => {
  if (plan.deleteIds.length > 0) {
    await trx.deleteFrom('MealLog').where('id', 'in', plan.deleteIds).execute();
  }

  for (const placement of plan.updates) {
    await trx
      .updateTable('MealLog')
      .set({
        consumedAt: placement.consumedAt,
        mealType: placement.mealType,
        updatedAt: new Date().toISOString(),
      })
      .where('id', '=', placement.id)
      .execute();
  }

  if (plan.inserts.length > 0) {
    await trx.insertInto('MealLog').values(plan.inserts).execute();
  }
};

export interface CreateMealLogInput {
  mealId: string;
  consumedAmount: number;
  /** Unit the amount was logged in (a servingUnits name); null/omitted = base unit. */
  consumedAmountUnit?: string | null;
  consumedAt: string;
  mealType?: string | null;
}

export interface UpdateMealLogInput {
  mealId?: string;
  consumedAmount?: number;
  consumedAmountUnit?: string | null;
  consumedAt?: string;
  mealType?: string | null;
}

export const MealLogMutations = {
  /**
   * Create a new meal log entry
   */
  create: async (
    db: LocalDatabase,
    input: CreateMealLogInput,
  ): Promise<MealLogRecord> => {
    const id = RandomUtils.uuid();
    const now = new Date().toISOString();

    const mealLog: MealLogRecord = {
      id,
      userId: '', // Will be set by PowerSync sync rules
      niceId: null, // Will be generated by database
      mealId: input.mealId,
      consumedAmount: input.consumedAmount.toString(),
      consumedAmountUnit: input.consumedAmountUnit ?? null,
      consumedAt: input.consumedAt,
      mealType: input.mealType || null,
      createdAt: now,
      updatedAt: now,
    };

    await db.insertInto('MealLog').values(mealLog).execute();

    return mealLog;
  },

  /**
   * Update an existing meal log entry
   */
  update: async (
    db: LocalDatabase,
    id: string,
    input: UpdateMealLogInput,
  ): Promise<void> => {
    const now = new Date().toISOString();

    const updateData: Partial<MealLogRecord> = {
      updatedAt: now,
    };

    if (input.mealId !== undefined) {
      updateData.mealId = input.mealId;
    }
    if (input.consumedAmount !== undefined) {
      updateData.consumedAmount = input.consumedAmount.toString();
    }
    if (input.consumedAmountUnit !== undefined) {
      updateData.consumedAmountUnit = input.consumedAmountUnit;
    }
    if (input.consumedAt !== undefined) {
      updateData.consumedAt = input.consumedAt;
    }
    if (input.mealType !== undefined) {
      updateData.mealType = input.mealType;
    }

    await db
      .updateTable('MealLog')
      .set(updateData)
      .where('id', '=', id)
      .execute();
  },

  /**
   * Delete a meal log by ID
   * Note: MealLog doesn't have deletedAt field in schema, so this performs hard delete
   */
  delete: async (db: LocalDatabase, id: string): Promise<void> => {
    await db.deleteFrom('MealLog').where('id', '=', id).execute();
  },

  /**
   * Applies a planned copy/move in ONE transaction: deletes, then updates, then
   * inserts. A given plan only ever populates one or two of the three.
   *
   * Mirrors the transactional shape of `WorkoutMutations.duplicateWorkout`.
   */
  applyCopyPlan: async (
    db: LocalDatabase,
    plan: MealLogCopyPlan,
  ): Promise<void> => {
    await db.transaction().execute(async (trx) => {
      await applyCopyPlanIn(trx, plan);
    });
  },

  /**
   * Plans and applies a copy or a move over `sourceIds`, returning what landed
   * where plus everything undo needs.
   *
   * **The source rows are re-read INSIDE the transaction**, and only rows that
   * still exist are planned over. Without that, a row deleted on another device
   * and already synced down would still be counted, and the summary's "6 items
   * moved to Dinner" would be a factual claim about a row the server discarded.
   * This does not close the window — a delete still in flight cannot be seen —
   * and is not claimed to; it converts the common case from a silent
   * overstatement into a correct one.
   *
   * The re-read uses a raw builder on the transaction handle rather than
   * `MealLogQueries.findByIds`: that is a `QueryBuilderSpec` for
   * `useLivePowerSyncQuery`, whereas inside a transaction the handle is a
   * `Transaction<Database>`.
   */
  copyLogs: async (
    db: LocalDatabase,
    sourceIds: string[],
    destination: MealLogCopyDestination,
    mode: MealLogCopyMode,
  ): Promise<MealLogCopyResult> => {
    return await db.transaction().execute(async (trx) => {
      const sources =
        sourceIds.length === 0
          ? []
          : ((await trx
              .selectFrom('MealLog')
              .where('id', 'in', sourceIds)
              .selectAll()
              .execute()) as MealLogRecord[]);

      const plan = MealLogCopyPlanner.plan(
        sources,
        destination,
        mode,
        new Date().toISOString(),
        RandomUtils.uuid,
      );

      // Captured BEFORE the writes land, so undo restores the pre-move state.
      const priorStates: MealLogPlacement[] =
        mode === 'move'
          ? sources.map((source) => ({
              id: source.id,
              consumedAt: source.consumedAt as string,
              mealType: (source.mealType as string | null) ?? null,
            }))
          : [];

      await applyCopyPlanIn(trx, plan);

      // Carries `mealId` so the summary can OPTIONALLY name what landed. For a
      // move the placements have no mealId of their own, so it is looked up
      // from the source rows that were just re-read.
      const mealIdBySourceId = new Map(
        sources.map((source) => [source.id, source.mealId as string | null]),
      );
      const landed: MealLogSummaryRow[] =
        mode === 'move'
          ? plan.updates.map((placement) => ({
              ...placement,
              mealId: mealIdBySourceId.get(placement.id) ?? null,
            }))
          : plan.inserts.map((row) => ({
              id: row.id,
              consumedAt: row.consumedAt as string,
              mealType: (row.mealType as string | null) ?? null,
              mealId: row.mealId as string | null,
            }));

      return {
        mode,
        destination,
        insertedIds: plan.inserts.map((row) => row.id),
        priorStates,
        groups: MealLogCopyPlanner.summarise(landed),
      };
    });
  },

  /** Reverses an applied operation — one-shot, driven from the summary modal. */
  revertCopy: async (
    db: LocalDatabase,
    result: MealLogCopyResult,
  ): Promise<void> => {
    const plan = MealLogCopyPlanner.planRevert(result);
    await db.transaction().execute(async (trx) => {
      await applyCopyPlanIn(trx, plan);
    });
  },
};
